import { GlButton, GlSprintf, GlLink, GlFormCheckbox } from '@gitlab/ui';
import { mapState, mapActions } from 'pinia';
import { mergeUrlParams } from '~/lib/utils/url_utility';
import { getRawPath } from '~/diffs/utils/diff_file';
import { diffLineToString } from '~/diffs/utils/diff_line';
import { __ } from '~/locale';
import MarkdownEditor from '~/vue_shared/components/markdown/markdown_editor.vue';
import { trackSavedUsingEditor } from '~/vue_shared/components/markdown/tracking';
import { useBatchComments } from '~/batch_comments/store';
import { useNotes } from '~/notes/store/legacy_notes';
import eventHub from '../event_hub';
import issuableStateMixin from '../mixins/issuable_state';
import resolvable from '../mixins/resolvable';
import { COMMENT_FORM } from '../i18n';
import CommentFieldLayout from './comment_field_layout.vue';
export default {
    i18n: COMMENT_FORM,
    name: 'NoteForm',
    components: {
        MarkdownEditor,
        CommentFieldLayout,
        GlButton,
        GlSprintf,
        GlLink,
        GlFormCheckbox
    },
    mixins: [
        issuableStateMixin,
        resolvable
    ],
    props: {
        noteBody: {
            type: String,
            required: false,
            default: ''
        },
        noteId: {
            type: [
                String,
                Number
            ],
            required: false,
            default: ''
        },
        saveButtonTitle: {
            type: String,
            required: false,
            default: __('Save comment')
        },
        discussion: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        lineCode: {
            type: String,
            required: false,
            default: ''
        },
        resolveDiscussion: {
            type: Boolean,
            required: false,
            default: false
        },
        line: {
            type: Object,
            required: false,
            default: null
        },
        lines: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        note: {
            type: Object,
            required: false,
            default: null
        },
        diffFile: {
            type: Object,
            required: false,
            default: null
        },
        helpPagePath: {
            type: String,
            required: false,
            default: ''
        },
        autosaveKey: {
            type: String,
            required: false,
            default: ''
        },
        showSuggestPopover: {
            type: Boolean,
            required: false,
            default: false
        },
        isDraft: {
            type: Boolean,
            required: false,
            default: false
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: true
        },
        restoreFromAutosave: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'cancel-form',
        'handle-form-update',
        'handle-form-update-add-to-review',
        'handle-suggest-dismissed'
    ],
    data () {
        return {
            updatedNoteBody: this.noteBody,
            conflictWhileEditing: false,
            isSubmitting: false,
            isResolving: this.resolveDiscussion,
            isUnresolving: !this.resolveDiscussion,
            // eslint-disable-next-line vue/no-unused-properties -- `resolveAsThread` is used by the `resolvable` mixin
            resolveAsThread: true,
            isSubmittingWithKeydown: false,
            formFieldProps: {
                id: 'note_note',
                name: 'note[note]',
                'aria-label': __('Reply to comment'),
                placeholder: this.$options.i18n.bodyPlaceholder,
                class: 'note-textarea js-gfm-input js-note-text markdown-area js-vue-issue-note-form',
                'data-testid': 'reply-field'
            }
        };
    },
    computed: {
        // Diff comments cannot be internal notes, so keep /internal_note out of the autocomplete.
        excludedCommands () {
            return this.diffParams ? 'internal_note' : null;
        },
        ...mapState(useNotes, [
            'getDiscussionCurrentUserLastNote',
            'getNoteableData',
            'getNotesDataByProp'
        ]),
        ...mapState(useBatchComments, [
            'hasDrafts',
            'isMergeRequest'
        ]),
        autocompleteDataSources () {
            return gl.GfmAutoComplete?.dataSources;
        },
        showBatchCommentsActions () {
            return this.isMergeRequest && this.noteId === '' && !this.discussion.for_commit;
        },
        showResolveDiscussionToggle () {
            if (!this.discussion?.notes) return false;
            return this.discussion?.notes.filter((n)=>n.resolvable).some((n)=>n.current_user?.can_resolve_discussion) || this.isDraft;
        },
        noteHash () {
            if (this.noteId) {
                return `#note_${this.noteId}`;
            }
            return '#';
        },
        noteableType () {
            return this.getNoteableData.noteableType;
        },
        diffParams () {
            if (this.diffFile) {
                return {
                    filePath: this.diffFile.file_path,
                    refs: this.diffFile.diff_refs
                };
            }
            if (this.note && this.note.position) {
                return {
                    filePath: this.note.position.new_path,
                    refs: this.note.position
                };
            }
            if (this.discussion && this.discussion.diff_file) {
                return {
                    filePath: this.discussion.diff_file.file_path,
                    refs: this.discussion.diff_file.diff_refs
                };
            }
            return null;
        },
        markdownPreviewPath () {
            const notable = this.getNoteableData.preview_note_path;
            const previewSuggestions = Boolean(this.line && this.diffParams);
            const params = previewSuggestions ? {
                preview_suggestions: previewSuggestions,
                line: this.line.new_line,
                file_path: this.diffParams.filePath,
                base_sha: this.diffParams.refs.base_sha,
                start_sha: this.diffParams.refs.start_sha,
                head_sha: this.diffParams.refs.head_sha
            } : {};
            return mergeUrlParams(params, notable);
        },
        markdownDocsPath () {
            return this.getNotesDataByProp('markdownDocsPath');
        },
        isDisabled () {
            return !this.updatedNoteBody.length || this.isSubmitting;
        },
        isInternalNote () {
            return this.discussion.confidential;
        },
        canSuggest () {
            return Boolean(this.getNoteableData.can_receive_suggestion && this.line && this.line.can_receive_suggestion);
        },
        changedCommentText () {
            return {
                text: __('This comment changed after you started editing it. Review the %{startTag}updated comment%{endTag} to ensure information is not lost.'),
                placeholder: {
                    link: [
                        'startTag',
                        'endTag'
                    ]
                }
            };
        },
        codeSuggestionsConfig () {
            const { line, lines, diffFile } = this;
            const selectedLines = lines.length ? lines : [
                line
            ].filter(Boolean);
            return {
                canSuggest: this.canSuggest,
                lines: selectedLines.map(diffLineToString),
                lineType: line?.type ?? '',
                showPopover: this.showSuggestPopover,
                blobRawPath: diffFile?.view_path ? getRawPath(diffFile) : null,
                lineRange: selectedLines.length ? {
                    start: selectedLines[0].new_line,
                    end: selectedLines[selectedLines.length - 1].new_line
                } : null
            };
        },
        shouldDisableField () {
            return this.isSubmitting;
        }
    },
    watch: {
        noteBody () {
            if (this.updatedNoteBody === this.noteBody) {
                this.updatedNoteBody = this.noteBody;
            } else {
                this.conflictWhileEditing = true;
            }
        }
    },
    mounted () {
        this.updatePlaceholder();
    },
    methods: {
        // eslint-disable-next-line vue/no-unused-properties -- toggleResolveNote() used by the `Resolvable` mixin
        ...mapActions(useNotes, [
            'toggleResolveNote'
        ]),
        shouldToggleResolved (beforeSubmitDiscussionState) {
            return this.showResolveDiscussionToggle && beforeSubmitDiscussionState !== this.newResolvedState();
        },
        newResolvedState () {
            return this.discussionResolved && !this.isUnresolving || !this.discussionResolved && this.isResolving;
        },
        editMyLastNote () {
            if (this.updatedNoteBody === '') {
                const lastNoteInDiscussion = this.getDiscussionCurrentUserLastNote(this.discussion);
                if (lastNoteInDiscussion) {
                    eventHub.$emit('enter-edit-mode', {
                        noteId: lastNoteInDiscussion.id
                    });
                }
            }
        },
        cancelHandler (shouldConfirm = false) {
            // check if any dropdowns are active before sending the cancelation event
            if (!this.$refs.markdownEditor.$el.querySelector('textarea')?.classList.contains('at-who-active')) {
                this.$emit('cancel-form', shouldConfirm, this.noteBody !== this.updatedNoteBody);
            }
        },
        updatePlaceholder () {
            this.formFieldProps.placeholder = this.isInternalNote ? this.$options.i18n.bodyPlaceholderInternal : this.$options.i18n.bodyPlaceholder;
        },
        onInput (value) {
            this.updatedNoteBody = value;
        },
        // eslint-disable-next-line vue/no-unused-properties -- `append` is called via `$refs.noteForm.append()` in noteable_discussion.vue
        append (value) {
            this.$refs.markdownEditor.append(value);
        },
        handleKeySubmit (forceUpdate = false) {
            if (this.showBatchCommentsActions && !forceUpdate) {
                this.handleAddToReview();
            } else {
                this.isSubmittingWithKeydown = true;
                this.handleUpdate();
            }
            this.updatedNoteBody = '';
        },
        handleUpdate () {
            const beforeSubmitDiscussionState = this.discussionResolved;
            this.isSubmitting = true;
            trackSavedUsingEditor(this.$refs.markdownEditor.isContentEditorActive, `${this.noteableType}_note`);
            this.$emit('handle-form-update', this.updatedNoteBody, this.$refs.editNoteForm, (error, { resolvedStateSaved = false } = {})=>{
                this.isSubmitting = false;
                if (!resolvedStateSaved && this.shouldToggleResolved(beforeSubmitDiscussionState)) {
                    this.resolveHandler(beforeSubmitDiscussionState);
                }
            }, this.discussionResolved ? !this.isUnresolving : this.isResolving);
        },
        handleAddToReview () {
            const clickType = this.hasDrafts ? 'note-form-add-to-review' : 'note-form-start-review';
            // check if draft should resolve thread
            const shouldResolve = this.discussionResolved && !this.isUnresolving || !this.discussionResolved && this.isResolving;
            this.isSubmitting = true;
            eventHub.$emit(clickType, {
                name: clickType
            });
            this.$emit('handle-form-update-add-to-review', this.updatedNoteBody, shouldResolve, this.$refs.editNoteForm, ()=>{
                this.isSubmitting = false;
            });
        }
    }
};
